import * as dynamicFieldsService from '../../../../services/api/dynamic-fields-api';

// disables component when true
export const DISABLE_DYNAMIC_FIELDS = false;

// enum for FieldType value from dto
// single text = ignore lookup values
// lookup should only work with dropdowns
// later on can add lookup to single as autocomplete
export const FIELD_TYPES = {
    singleText: 'Short Text Entry',
    multiText: 'Long Text Entry',
    dropDown: 'Dropdown',
    radioButtons: 'Radio Buttons',
    checkbox: 'Checkbox',
    date: 'Date',
    time: 'Time',
    integer: 'Numeric (Integer)',
    decimal: 'Numeric (Decimal)',
    table: 'Table',
    toggle: 'Toggle',
    file: 'File',
    url: 'URL',
};

// enum for FieldRegexDataType value from dto
export const FIELD_REGEX_DATA_TYPES = {
    any: null,
    string: 'String', // TODO: temp value
    int: 'Integer',
    number: 'Decimal', // TODO: temp value
};

/** DynamicFieldDto interface:
 *  {
 *      Id: int
 *      Label: string
 *      Placeholder: string?
 *      CssClass: string?
 *      FieldMaskId: int?
 *      FieldMask: string?
 *      FieldRegexId: int?
 *      FieldRegex: string?
 *      FieldRegexDataType: string?
 *      FieldRegexDescription: string?
 *      Visible: boolean
 *      Deleted: boolean
 *      Lookup: boolean
 *      LookupValues: string[]
 *      FieldHelp: string?
 *      FieldType: "SingleText" | "MultiText" | "DropDown"
 *      Required: bool
 *      ObjectId: int
 *      DataId: int
 *      Data: string
 *      Order: int
 *
 *      touched: boolean        // used to show errors; field does not exist on backend
 *  }
 */

// projectTypeId: Integer
// taskTypeId: Integer
// objectId: Integer?; id of comment of associated dynamic fields to retrieve data
export async function fetchDynamicFields({ projectTypeId, taskTypeId, objectId }) {
    try {
        const dynamicFields = await dynamicFieldsService.getDynamicFields({
            projectTypeId,
            taskTypeId,
            objectType: dynamicFieldsService.OBJECT_TYPES.taskComment,
            objectId,
        });
        return dynamicFields.map((field) => ({
            ...field,
            Data: field.Data ?? '',
            touched: false,
        }));
    } catch (error) {
        // will display nothing on component since dynamicFields will be empty
        return [];
    }
}

// field: DynamicFieldDto
export function isMaskValid(field) {
    const { Data, FieldMask } = field;

    if (!FieldMask) return true;
    if (Data.length !== FieldMask.length) return false;

    for (let i = 0; i < FieldMask.length; i++) {
        const maskCh = FieldMask[i];
        const fieldCh = Data[i];

        switch (maskCh) {
            case '#':
                if (fieldCh < '0' || fieldCh > '9') return false;
                break;
            default:
                if (maskCh !== fieldCh) return false;
        }
    }

    return true;
}

// field: DynamicFieldDto
export function isRegexValid(field) {
    const { Data, FieldRegex, FieldRegexDataType } = field;

    if (FieldRegex) {
        const regex = new RegExp(FieldRegex);
        if (!regex.test(Data)) return false;
    }

    switch (FieldRegexDataType) {
        case FIELD_REGEX_DATA_TYPES.int:
            return Number.isInteger(Number(Data));
        case FIELD_REGEX_DATA_TYPES.string:
            return typeof Data === 'string' || Data instanceof String;
        case FIELD_REGEX_DATA_TYPES.number:
            const dataNumber = Number(Data);
            return typeof dataNumber === 'number' && !Number.isNaN(dataNumber);
        default:
            return true;
    }
}

export function isFieldTypeValid(field) {
    switch (field.FieldType) {
        case FIELD_TYPES.time: {
            // split "HH:MM" into [HH, MM] and convert them to numbers
            const [HH, MM] = field.Data.split(':').map(Number);
            // check if either are falsy and not 0
            if ([HH, MM].some((val) => !val && val !== 0)) return false;

            else return HH >= 0 && MM >= 0 && MM <= 59;
        }
        default:
        // should not reach here
    }
    return true;
}

// field: DynamicFieldDto
export function isDynamicTableFieldDataValid(field, row, columnId) {
    if (!field.Table) return;
    if (!field.Table.Rows) return;

    const existingRow = field.Table.Rows.find((r) => r.Row === row);

    if (!existingRow) return;
    if (!existingRow.Data) return;

    const rowData = existingRow.Data.find((d) => d.ColumnId == columnId);

    if (!rowData) return;

    if (rowData.ColumnType === 'Date') {
        const isDate = (date) => {
            return (new Date(date).toString() !== "Invalid Date") && !isNaN(new Date(date));
        }

        return rowData.Value && isDate(rowData.Value);
    }
}

export function isDynamicFieldValid(field) {
    const { Required } = field;

    if (field.FieldType === FIELD_TYPES.table) {
        const hasRows = field.Table.Rows?.length > 0;
        const hasValues = field.Table.Rows?.some((rowData) => rowData.Value !== '');

        return Required ? hasRows && hasValues : true;
    } else {
        const { Data } = field;
        const isEmpty = Data === '' || Data == null;
        const isWhitespace = !/\S/.test(Data);
        const isPassedChecks = [isMaskValid, isRegexValid, isFieldTypeValid].every((func) =>
            func(field)
        );

        return Required ? isPassedChecks && !isEmpty && !isWhitespace : isEmpty || isPassedChecks;
    }
}

// dynamicFields: DynamicFieldDto[]
export function isDynamicFieldsValid(dynamicFields) {
    for (const field of dynamicFields) {
        if (!isDynamicFieldValid(field)) return false;
    }

    return true;
}

export function isAllDynamicFieldsEmpty(dynamicFields) {
    for (const field of dynamicFields) {
        const { Data } = field;
        const isEmpty = Data === '';
        const isWhitespace = !/\S/.test(Data);

        if (!isEmpty && !isWhitespace) return false;
    }
    return true;
}

export function isUserViewer(user, taskMembers) {
    if (!user || !taskMembers) return false;
    const userInTask = taskMembers.find((member) => member.userID === user.profile.userID);

    return (userInTask?.permission === 2 || userInTask?.view) ?? false;
}
